import React, { useState, useEffect, useRef } from 'react';
import { UserRole } from '../types';
import { AVAILABLE_PERMISSIONS } from '../types/role';
import { Crown, User, Users, Shield, Lock, AlertCircle, Settings, Power, Trash2, Edit } from 'lucide-react';
import { useRoleProtection } from '../hooks/useRoleProtection';

interface RoleCardProps {
  role: UserRole;
  onEdit: (role: UserRole) => void;
  onToggleActive: (roleId: string, isActive: boolean) => void;
  onDelete: (roleId: string) => void;
}

const RoleCard: React.FC<RoleCardProps> = ({
  role,
  onEdit,
  onToggleActive,
  onDelete
}) => {
  const { userRole } = useRoleProtection();
  const [actionsOpen, setActionsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActionsOpen(false);
      }
    };

    if (actionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [actionsOpen]);

  // Check if current user is a Business Owner and if this is the Business Owner role
  const isBusinessOwnerRole = role.role === 'Business Owner';
  const isCurrentUserBusinessOwner = userRole?.name === 'Business Owner';
  const isSelfModification = isBusinessOwnerRole && isCurrentUserBusinessOwner;

  // Role protection logic for roles
  const roleProtection = {
    isProtected: isSelfModification,
    reason: isSelfModification ? 'Cannot modify your own Business Owner role' : undefined,
    canEdit: !isSelfModification,
    canDeactivate: !isSelfModification,
    canDelete: !isSelfModification
  };

  // Check if buttons should be disabled
  const canEdit = roleProtection.canEdit;
  const canDeactivate = roleProtection.canDeactivate;
  const canDelete = roleProtection.canDelete;

  const getPermissionNames = (permissionNames: string[]): string[] => {
    if (!permissionNames || !Array.isArray(permissionNames)) {
      return [];
    }
    return permissionNames;
  };

  const getPermissionCategories = (permissionNames: string[]): string[] => {
    if (!permissionNames || !Array.isArray(permissionNames)) {
      return [];
    }
    const categories = permissionNames
      .map(name => AVAILABLE_PERMISSIONS.find(p => p.id === name)?.category)
      .filter(Boolean) as string[];
    return Array.from(new Set(categories)); // Remove duplicates
  };

  const formatCategoryName = (category: string): string => {
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getRoleIcon = (roleType: string) => {
    switch (roleType) {
      case 'Business Owner':
        return <Crown className="h-5 w-5 text-yellow-600" />;
      case 'Professional':
        return <User className="h-5 w-5 text-blue-600" />;
      case 'Manager':
        return <Shield className="h-5 w-5 text-purple-600" />;
      case 'Assistant':
        return <Users className="h-5 w-5 text-blue-500" />;
      case 'CUSTOMER':
        return <Users className="h-5 w-5 text-green-600" />;
      default:
        return <Shield className="h-5 w-5 text-gray-600" />;
    }
  };

  const permissionNames = getPermissionNames(role.permissions);
  const permissionCategories = getPermissionCategories(role.permissions);

  return (
    <div className={`rounded-lg shadow-sm border border-gray-200 overflow-visible hover:shadow-md transition-shadow duration-200 flex flex-col relative ${
      !role.isActive 
        ? 'bg-gray-50 border-gray-200' 
        : 'bg-white border-gray-200'
    }`}>
      {/* Gear icon in top right corner - positioned outside grayed content */}
      <div className="absolute top-3 right-3 z-10 role-actions-dropdown-container" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setActionsOpen(o => !o)}
          className={`p-1.5 rounded transition-colors ${actionsOpen ? 'bg-gray-100 text-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
          title={(!canEdit && !canDeactivate && !canDelete) ? 'No actions available' : 'More actions'}
        >
          <Settings className="w-4 h-4" />
        </button>
        {actionsOpen && (
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999]">
            <div className="py-1">
              {canEdit && (
                <button
                  onClick={() => {
                    onEdit(role);
                    setActionsOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
              )}
              {canDeactivate && (
                <button
                  onClick={() => {
                    onToggleActive(role.id, !role.isActive);
                    setActionsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-2 ${
                    role.isActive
                      ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                      : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                  }`}
                >
                  <Power className="w-4 h-4" />
                  {role.isActive ? 'Deactivate' : 'Activate'}
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => {
                    onDelete(role.id);
                    setActionsOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              )}
              {!canEdit && !canDeactivate && !canDelete && (
                <div className="px-4 py-2 text-xs text-gray-500">
                  {roleProtection.reason || 'No actions available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Role Header */}
      <div className={`p-4 border-b border-gray-100 ${!role.isActive ? 'opacity-40 grayscale' : ''}`}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1 pr-8">
            <div className="flex items-center gap-2 mb-1">
              {getRoleIcon(role.role)}
              <h3 className="text-lg font-semibold text-gray-900 line-clamp-1">
                {role.role}
              </h3>
              {roleProtection.isProtected && (
                <Lock className="h-4 w-4 text-orange-500" />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                role.role === 'Business Owner' ? 'bg-yellow-100 text-yellow-800' :
                role.role === 'Professional' ? 'bg-blue-100 text-blue-800' :
                role.role === 'Manager' ? 'bg-purple-100 text-purple-800' :
                role.role === 'Assistant' ? 'bg-blue-100 text-blue-800' :
                role.role === 'CUSTOMER' ? 'bg-green-100 text-green-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {role.role === 'Business Owner' ? 'Full Access' :
                 role.role === 'Professional' ? 'Limited Access' :
                 role.role === 'Manager' ? 'Management Access' :
                 role.role === 'Assistant' ? 'Basic Access' :
                 role.role === 'CUSTOMER' ? 'Read Only' : 'User Role'}
              </span>
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                role.isActive
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {role.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
        </div>
        
        <p className="text-gray-600 text-sm line-clamp-2">
          {role.role === 'Business Owner' ? 'Full access to all business features and settings' :
           role.role === 'Professional' ? 'Can manage appointments, services, and their own schedule' :
           role.role === 'Manager' ? 'Management access to business operations and staff' :
           role.role === 'Assistant' ? 'Basic access for assistants and support staff' :
           role.role === 'CUSTOMER' ? 'Can view appointments and book services' :
           `${role.permissions?.length || 0} permissions assigned`}
        </p>
        
      </div>

      {/* Permissions Summary */}
      <div className={`p-4 flex-1 ${!role.isActive ? 'opacity-40 grayscale' : ''}`}>
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-2">Permission Categories:</p>
          <div className="flex flex-wrap gap-1">
            {permissionCategories.slice(0, 4).map((category, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
              >
                {formatCategoryName(category)}
              </span>
            ))}
            {permissionCategories.length > 4 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                +{permissionCategories.length - 4} more
              </span>
            )}
          </div>
        </div>

        <div className="mb-4">
          <p className="text-xs font-medium text-gray-500 mb-2">Permissions ({permissionNames.length}):</p>
          <div className="text-xs text-gray-600 line-clamp-2">
            {permissionNames.slice(0, 3).join(', ')}
            {permissionNames.length > 3 && ` and ${permissionNames.length - 3} more`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleCard; 